import _ from 'lodash';
import { FETCH_JOBS, FETCH_JOB, CREATE_JOB, REMOVE_JOB } from './types';
import { setErrorMessage } from './errorMessage';
import api from '../apis/api';
import store from '../store'; // Still needed for jobs state
// Navigation should be handled in components using useNavigate hook
import getMessageFromApiError from '../util/getMessageFromApiError';
import STATUSES from '../components/statuses';

const isAuthError = (error) => Boolean(error?.isAuthError);

export const fetchJobs = (project_uuid) => async (dispatch) => {
  try {
    if (!project_uuid) {
      throw new Error('project_uuid is required');
    }
    const res = await api.get('/job', { params: { project_uuid } });
    dispatch({
      type: FETCH_JOBS,
      payload: res.data,
    });
  } catch (error) {
    if (isAuthError(error)) {
      return;
    }
    const message = getMessageFromApiError(error);
    dispatch(setErrorMessage(message));
    throw new Error(`API Error - ${message}`);
  }
};

export const fetchJob = (project_uuid, id) => async (dispatch) => {
  if (!project_uuid) {
    throw new Error('project_uuid is required');
  }
  try {
    const res = await api.get(`/job/${id}`, { params: { project_uuid } });
    if (_.isEmpty(res.data)) {
      dispatch({ type: REMOVE_JOB, payload: id });
    } else {
      dispatch({
        type: FETCH_JOB,
        payload: res.data,
      });
    }
  } catch (error) {
    if (error.response && error.response.status === 404) {
      dispatch({ type: REMOVE_JOB, payload: id });
    } else if (isAuthError(error)) {
      return;
    } else {
      const message = getMessageFromApiError(error);
      dispatch(setErrorMessage(message));
      throw new Error(`API Error - ${message}`);
    }
  }
};

export const submitJob = (project_uuid, data) => async (dispatch) => {
  try {
    if (!data.project_uuid) {
      if (!project_uuid) {
        throw new Error('project_uuid is required');
      }
      data.project_uuid = project_uuid;
    }
    const res = await api.post('/job', data);

    // Navigation moved to component - use navigate hook in the component that calls this action
    // navigate(`/jobs/${res.data.type}`);
    if (project_uuid && res.data.project_uuid !== project_uuid) {
      return;
    }
    dispatch({
      type: CREATE_JOB,
      payload: res.data,
    });
  } catch (error) {
    const message = getMessageFromApiError(error);
    dispatch(setErrorMessage(message));
    throw new Error(`API Error - ${message}`);
  }
};

export const updateJob = (project_uuid, id, values) => async (dispatch) => {
  if (!project_uuid) {
    throw new Error('project_uuid is required');
  }
  try {
    const res = await api.patch(`/job/${id}`, values, {
      params: { project_uuid },
    });
    dispatch({
      type: FETCH_JOB,
      payload: res.data,
    });
  } catch (error) {
    const message = getMessageFromApiError(error);
    dispatch(setErrorMessage(message));
    throw new Error(`API Error - ${message}`);
  }
};

export const terminateJob = (project_uuid, id) => async (dispatch) => {
  if (!project_uuid) {
    throw new Error('project_uuid is required');
  }
  const job = store.getState().jobs.find((job) => job.job_uuid === id);
  try {
    if (
      ![STATUSES.STOPPED, STATUSES.FINISHED, STATUSES.CACHING].includes(
        job.status
      )
    ) {
      await api.delete(`/job/${id}`, { params: { force: true, project_uuid } });
      dispatch({ type: REMOVE_JOB, payload: id });
    } else {
      await api.delete(`/job/${id}`, { params: { project_uuid } });
      dispatch(fetchJob(project_uuid, id));
    }
  } catch (error) {
    if (error.response && error.response.status === 404) {
      dispatch({ type: REMOVE_JOB, payload: id });
    } else {
      const message = getMessageFromApiError(error);
      dispatch(setErrorMessage(message));
      throw new Error(`API Error - ${message}`);
    }
  }
};

export const updateJobWorker =
  (project_uuid, job_uuid, job_worker_uuid, values) => async (dispatch) => {
    try {
      if (!project_uuid) {
        throw new Error('project_uuid is required');
      }
      await api.patch(`/job/${job_uuid}/worker/${job_worker_uuid}`, values, {
        params: { project_uuid },
      });
      dispatch(fetchJob(project_uuid, job_uuid));
    } catch (error) {
      const message = getMessageFromApiError(error);
      dispatch(setErrorMessage(message));
      throw new Error(`API Error - ${message}`);
    }
  };
